import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import {
  BinanceWithdrawalRow,
  BinanceWithdrawalsSearchResponse,
  BinanceWithdrawalsSearchService,
} from '../../services/binance-withdrawals-search.service';

@Component({
  selector: 'app-binance-search-by-wallet',
  templateUrl: './binance-search-by-wallet.component.html',
  styleUrls: ['./binance-search-by-wallet.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BinanceSearchByWalletComponent implements OnInit {
  form: FormGroup;

  rows: BinanceWithdrawalRow[] = [];
  total = 0;
  page = 1;
  limit = 20;
  totalPages = 0;
  rangeStart: string | null = null;
  rangeEnd: string | null = null;
  walletSearched: string | null = null;

  loading = false;
  exporting = false;
  hasSearched = false;
  errorMessage: string | null = null;

  pageSizeOptions = [20, 50, 100];

  displayedColumns: string[] = [
    'applyTime',
    'completeTime',
    'amount',
    'fee',
    'network',
    'status',
    'user',
    'rail',
    'txId',
    'address',
  ];

  constructor(
    private fb: FormBuilder,
    private service: BinanceWithdrawalsSearchService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      walletAddress: [''],
      startDate: [null],
      endDate: [null],
    });
  }

  ngOnInit(): void {
    // Carga inicial: todos los retiros de los últimos 90 días sin filtro
    // de wallet. El operador puede luego acotar.
    this.search(true);
  }

  private formatStartForApi(d: Date | string | null): string | undefined {
    if (!d) return undefined;
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return undefined;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0,
    ).toISOString();
  }

  private formatEndForApi(d: Date | string | null): string | undefined {
    if (!d) return undefined;
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return undefined;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999,
    ).toISOString();
  }

  search(resetPage = true): void {
    if (resetPage) this.page = 1;

    const raw = this.form.value;
    const wallet = String(raw.walletAddress || '').trim();
    const startDate = this.formatStartForApi(raw.startDate);
    const endDate = this.formatEndForApi(raw.endDate);

    this.loading = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.service
      .search({
        walletAddress: wallet,
        startDate,
        endDate,
        page: this.page,
        limit: this.limit,
      })
      .subscribe({
        next: (res: BinanceWithdrawalsSearchResponse) => {
          this.rows = res.data;
          this.total = res.meta.total;
          this.page = res.meta.page;
          this.limit = res.meta.limit;
          this.totalPages = res.meta.totalPages;
          this.rangeStart = res.meta.rangeStart;
          this.rangeEnd = res.meta.rangeEnd;
          this.walletSearched = res.meta.walletAddress;
          this.hasSearched = true;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.loading = false;
          this.errorMessage =
            err?.message || 'Error al consultar Binance';
          this.snackBar.open(this.errorMessage!, 'OK', { duration: 5000 });
          this.cdr.markForCheck();
        },
      });
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;
    this.search(false);
  }

  exportToExcel(): void {
    const raw = this.form.value;
    const wallet = String(raw.walletAddress || '').trim();
    const startDate = this.formatStartForApi(raw.startDate);
    const endDate = this.formatEndForApi(raw.endDate);

    this.exporting = true;
    this.cdr.markForCheck();

    this.service
      .exportExcel({ walletAddress: wallet, startDate, endDate })
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const safeWallet = wallet
            ? '_' + wallet.replace(/[^a-zA-Z0-9]/g, '').slice(0, 12)
            : '';
          const ts = new Date().toISOString().split('T')[0];
          a.download = `retiros_binance${safeWallet}_${ts}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.exporting = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.exporting = false;
          this.snackBar.open(
            err?.message || 'Error al exportar Excel',
            'OK',
            { duration: 5000 },
          );
          this.cdr.markForCheck();
        },
      });
  }

  trackByBinanceId(_: number, row: BinanceWithdrawalRow): string {
    return row.binanceId;
  }

  statusBadgeClass(status: number): string {
    switch (status) {
      case 6:
        return 'badge badge-success';
      case 4:
      case 0:
      case 2:
        return 'badge badge-warning';
      case 1:
      case 3:
      case 5:
        return 'badge badge-danger';
      default:
        return 'badge';
    }
  }

  formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    const y = d.getUTCFullYear();
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const h = String(d.getUTCHours()).padStart(2, '0');
    const mi = String(d.getUTCMinutes()).padStart(2, '0');
    const s = String(d.getUTCSeconds()).padStart(2, '0');
    return `${y}-${mo}-${day} ${h}:${mi}:${s} UTC`;
  }

  shortTxId(txId: string | null): string {
    if (!txId) return '—';
    if (txId.length <= 18) return txId;
    return `${txId.slice(0, 10)}…${txId.slice(-6)}`;
  }

  sourceLabel(source: string): string {
    switch (source) {
      case 'binance_withdrawal':
        return 'retiro USDT';
      case 'transaccion_cripto':
        return 'envío cripto';
      case 'beneficiario':
        return 'beneficiario';
      default:
        return source;
    }
  }

  sourceTooltip(source: string): string {
    switch (source) {
      case 'binance_withdrawal':
        return 'Match contra binance_withdrawals: el usuario retiró USDT desde su balance en la plataforma.';
      case 'transaccion_cripto':
        return 'Match contra transacciones (beneficiario_criptomoneda): envío cripto directo (CLP/VES → USDT a wallet externa).';
      case 'beneficiario':
        return 'Match sólo por wallet en beneficiario_criptomoneda; no se encontró transacción exacta.';
      default:
        return '';
    }
  }
}
